/**
 * Prompt-Aligned FAQ — Componente GEO
 * FAQ com perguntas reais que usuarios fazem a IA.
 */

import type { PromptAlignedFAQItem } from '@/lib/geo-factories';
import { createFAQSchema } from '@/lib/seo-utils';
import { useTranslation } from '@/hooks/useTranslation';
import { useAnalytics } from '@/hooks/useAnalytics';

interface PromptAlignedFAQProps {
  faqs: PromptAlignedFAQItem[];
  title?: string;
}

export function PromptAlignedFAQ({ faqs, title }: PromptAlignedFAQProps) {
  const { t } = useTranslation();
  const { trackOutbound } = useAnalytics();
  if (!faqs.length) return null;

  const schema = createFAQSchema(faqs.map(f => ({ q: f.pergunta, a: f.resposta })));

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
      <div className="my-8">
        <h3 className="font-display text-xl text-warm-dark mb-4">
          {title || t('widget.faq.title')}
        </h3>
        <div className="space-y-4">
          {faqs.map((faq, i) => (
            <details
              key={i}
              className="group bg-white border border-sand-200 rounded-lg overflow-hidden"
            >
              <summary className="flex items-center justify-between p-4 cursor-pointer font-body font-semibold text-warm-dark hover:bg-sand-50">
                {faq.pergunta}
                <span className="ml-2 text-ocean transition-transform group-open:rotate-180">
                  ▼
                </span>
              </summary>
              <div className="px-4 pb-4 font-body text-warm-gray text-sm leading-relaxed">
                {faq.resposta}
                {faq.link && (
                  <a
                    href={faq.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackOutbound(faq.link, faq.pergunta, 'faq_link')}
                    className="block mt-2 text-ocean hover:underline font-semibold"
                  >
                    {t('widget.faq.learnMore')}
                  </a>
                )}
              </div>
            </details>
          ))}
        </div>
      </div>
    </>
  );
}
